import { useCallback } from "react"

import { ThunderboltOutlined, WarningFilled } from "@ant-design/icons"
import { Alert, Form, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Icon from "../../../../../Icon"
import {
  clearPrefillSummary,
  saveSection,
  selectActiveBuildingBlocks,
  selectConnectedProcessors
} from "../redux/configure.slice"
import { removeVisualEvidence, uploadVisualEvidence } from "../redux/configure.thunks"
import SECTION_SCHEMA, {
  getSectionIntro,
  getSectionLabel,
  isExperimentalContentStep,
  resolveStepGroups,
  SECTION_GROUPS,
  SECTION_LABELS
} from "../schema/sections"
import { computePrunePatches } from "../utils/pruneHiddenAnswers"
import { evalShowIf, visibleQuestions } from "../utils/showIf"
import AutofillBanner from "./AutofillBanner"
import EnrichmentTab from "./EnrichmentTab"
import IntegrationsTab from "./IntegrationsTab"
import ProcessorSwitcher, { ActiveProcessorBanner } from "./ProcessorSwitcher"
import QuestionField from "./QuestionField/QuestionField"

import styles from "../configure.module.less"

const { Title, Text } = Typography

const SectionForm = ({ sectionKey, showErrors }) => {
  const dispatch = useDispatch()
  const buildingBlocks = useSelector(selectActiveBuildingBlocks)
  const connectedProcessors = useSelector(selectConnectedProcessors)
  const prefillRunning = useSelector((s) => s.disputesConfigure.ui?.prefillRunning)
  const prefillSummary = useSelector((s) => s.disputesConfigure.ui?.prefillSummary)
  // AutofillBanner takes this as a prop, so keep it stable.
  const dismissPrefillSummary = useCallback(() => dispatch(clearPrefillSummary()), [dispatch])
  const { t } = useTranslation()

  // The Evidence upload warning shows on the step that holds the evidence uploads: the
  // Evidence locker step (evidence_locker, reading the evidence_vault bucket), plus the
  // older keys the same step has gone by.
  const isEvidenceVault = ["evidence_locker", "evidence_vault", "auth_consent_evidence"].includes(
    sectionKey
  )
  const isIntegrations = sectionKey === "integrations"
  const isEnrichment = sectionKey === "enrichment"
  const isExpStep = isExperimentalContentStep(sectionKey)

  const schema = SECTION_SCHEMA[sectionKey] || []
  const sectionState = buildingBlocks?.[sectionKey] ?? {}
  const visible = visibleQuestions(schema, buildingBlocks)

  // Save a patch to a specific bucket, then stage the gate-driven prune against the
  // state AFTER this patch lands. Parameterized by section so an experimental step can
  // route each field's change to its own bucket (the prune already scans all sections).
  const handleChangeFor = (section) => (patch) => {
    dispatch(saveSection({ section, patch }))
    const nextBuildingBlocks = {
      ...buildingBlocks,
      [section]: { ...(buildingBlocks?.[section] ?? {}), ...patch }
    }
    const prunePatches = computePrunePatches(nextBuildingBlocks)
    for (const sKey of Object.keys(prunePatches)) {
      dispatch(saveSection({ section: sKey, patch: prunePatches[sKey] }))
    }
  }
  const handleUploadFor = (section) => (key, file) => {
    dispatch(uploadVisualEvidence({ section, slot: key, file }))
  }
  const handleRemoveFor = (section) => (key) => {
    dispatch(removeVisualEvidence({ section, slot: key }))
  }
  // Current-section handlers (the non-experimental single-bucket path).
  const handleChange = handleChangeFor(sectionKey)
  const handleUpload = handleUploadFor(sectionKey)
  const handleRemove = handleRemoveFor(sectionKey)

  // Surface the prefill banner on the step right after the website scrape:
  // general_information normally, Customer Experience in experimental mode.
  const showBanner = sectionKey === "general_information" || sectionKey === "customer_experience"

  // An intro can be a single string or an array of paragraphs (the evidence
  // locker uses two: the ask, then the why). Render an array as one <p> each.
  const intro = getSectionIntro(sectionKey)
  const introDescription = Array.isArray(intro)
    ? intro.map((p, i) => (
        <p key={i} className={styles.whyCalloutPara}>
          {t(p)}
        </p>
      ))
    : t(intro || "")

  // Integrate: a connect-your-stack step. No form questions; it renders the
  // payment-processor connector and the integrations directory (processor-card design).
  // Step 1 is the SHARED step: it's where the merchant decides how many processors
  // there will be, so the flow note tells them up front whether the remaining steps
  // will be one flow (a single processor) or repeated per processor. The per-processor
  // switcher deliberately does NOT appear here (it starts at step 2).
  if (isIntegrations) {
    const connectedCount = connectedProcessors?.length || 0
    const flowNote =
      connectedCount > 1
        ? t(
            "You've connected _n_ processors. You'll complete the next steps (identity, evidence and more) separately for each one.",
            { n: connectedCount }
          )
        : connectedCount === 1
          ? t("You've connected 1 processor. You'll complete the next steps once, for it.")
          : t(
              "Connect the payment processors you use. You'll then complete the next steps for each one you connect."
            )
    return (
      <Form
        layout="vertical"
        requiredMark={false}
        className={`recover-section-form ${styles.sectionFormWide}`}
      >
        <Title level={4} className={styles.sectionFormTitle}>
          {t(SECTION_LABELS[sectionKey])}
        </Title>
        <Alert
          type="info"
          showIcon
          className={styles.whyCallout}
          message={t("Why we're asking")}
          description={introDescription}
        />
        <div className={`${styles.flowNote}${connectedCount > 1 ? ` ${styles.isMulti}` : ""}`}>
          <Icon type="plugin_connected" className={styles.flowNoteIcon} style={{ fontSize: 20 }} />
          <Text className={styles.flowNoteText}>{flowNote}</Text>
        </div>
        <div className="recover-section-form_integrations">
          <IntegrationsTab />
        </div>
      </Form>
    )
  }

  // Enrichment: a per-processor connect step (support / shipping / marketing tools).
  // No form questions; renders the tool card grid. Because it is per processor, it
  // carries the same switcher + "Setting up <name>" banner as steps 3 onward.
  if (isEnrichment) {
    return (
      <Form
        layout="vertical"
        requiredMark={false}
        className={`recover-section-form ${styles.sectionFormWide}`}
      >
        <ProcessorSwitcher sectionKey={sectionKey} />
        <ActiveProcessorBanner />
        <Title level={4} className={styles.sectionFormTitle}>
          {t(SECTION_LABELS[sectionKey])}
        </Title>
        <Alert
          type="info"
          showIcon
          className={styles.whyCallout}
          message={t("Why we're asking")}
          description={introDescription}
        />
        <div className="recover-section-form_integrations">
          <EnrichmentTab />
        </div>
      </Form>
    )
  }

  // Render the section's questions under numbered category headers (SECTION_GROUPS).
  // A group whose questions are all hidden by showIf is skipped and the numbering
  // stays sequential. Any visible question not placed in a group renders ungrouped
  // (safety net), so nothing can silently disappear.
  const renderQ = (q) => (
    <QuestionField
      key={q.key}
      question={q}
      sectionState={sectionState}
      buildingBlocks={buildingBlocks}
      onChange={handleChange}
      onUpload={handleUpload}
      onRemove={handleRemove}
      showErrors={showErrors}
    />
  )
  // One experimental field: rendered from its OWN bucket (sectionState + onChange /
  // onUpload target field.section), so a step can mix fields from several buckets.
  const renderExpField = (f) => (
    <QuestionField
      key={`${f.section}.${f.key}`}
      question={f.def}
      sectionState={buildingBlocks?.[f.section] ?? {}}
      buildingBlocks={buildingBlocks}
      onChange={handleChangeFor(f.section)}
      onUpload={handleUploadFor(f.section)}
      onRemove={handleRemoveFor(f.section)}
      showErrors={showErrors}
    />
  )

  let groupNodes
  let leftover = []
  if (isExpStep) {
    // Experimental step: groups from the experimental mapping (each field carries its
    // source bucket). A group with title:null renders as a plain block (no numbered
    // header); a group whose fields are all hidden by showIf is skipped, and the
    // numbering stays sequential across the visible titled groups.
    let groupNum = 0
    groupNodes = resolveStepGroups(sectionKey)
      .map((g, gi) => {
        const fields = g.fields.filter((f) => evalShowIf(f.def.showIf, buildingBlocks))
        if (!fields.length) return null
        const titled = !!g.title
        if (titled) groupNum += 1
        const num = groupNum
        return (
          <section className={styles.sectionFormGroup} key={g.title || `ungrouped-${gi}`}>
            {titled ? (
              <div className={styles.sectionFormGroupHead}>
                <span className={styles.sectionFormGroupNum}>{num}</span>
                <span className={styles.sectionFormGroupHeading}>
                  <span className={styles.sectionFormGroupTitle}>{t(g.title)}</span>
                  {g.description ? (
                    <span className={styles.sectionFormGroupDesc}>{t(g.description)}</span>
                  ) : null}
                </span>
              </div>
            ) : null}
            <div className={styles.sectionFormGroupFields}>{fields.map(renderExpField)}</div>
          </section>
        )
      })
      .filter(Boolean)
  } else {
    const groups = SECTION_GROUPS[sectionKey] || []
    const visibleByKey = new Map(visible.map((q) => [q.key, q]))
    const placed = new Set()
    let groupNum = 0
    groupNodes = groups
      .map((g) => {
        const qs = g.keys.map((k) => visibleByKey.get(k)).filter(Boolean)
        if (!qs.length) return null
        qs.forEach((q) => placed.add(q.key))
        groupNum += 1
        const num = groupNum
        return (
          <section className={styles.sectionFormGroup} key={g.title}>
            <div className={styles.sectionFormGroupHead}>
              <span className={styles.sectionFormGroupNum}>{num}</span>
              <span className={styles.sectionFormGroupHeading}>
                <span className={styles.sectionFormGroupTitle}>{t(g.title)}</span>
                {g.description ? (
                  <span className={styles.sectionFormGroupDesc}>{t(g.description)}</span>
                ) : null}
              </span>
            </div>
            <div className={styles.sectionFormGroupFields}>{qs.map(renderQ)}</div>
          </section>
        )
      })
      .filter(Boolean)
    leftover = visible.filter((q) => !placed.has(q.key))
  }

  return (
    <Form layout="vertical" requiredMark={false} className="recover-section-form">
      <ProcessorSwitcher sectionKey={sectionKey} />
      <ActiveProcessorBanner />
      <div className={styles.sectionFormTitlerow}>
        <Title level={4} className={styles.sectionFormTitle}>
          {t(getSectionLabel(sectionKey))}
        </Title>
      </div>

      {showBanner && prefillRunning ? (
        <div className={`${styles.autofillBanner} ${styles.autofillBannerLoading}`}>
          <Spin size="small" />
          <Text>
            <ThunderboltOutlined /> {t("Reading your website to fill in the rest…")}
          </Text>
        </div>
      ) : null}
      {showBanner && !prefillRunning && prefillSummary ? (
        <AutofillBanner summary={prefillSummary} onDismiss={dismissPrefillSummary} />
      ) : null}

      <Alert
        type="info"
        showIcon
        className={styles.whyCallout}
        message={t("Why we're asking")}
        description={introDescription}
      />
      {isEvidenceVault ? (
        <Alert
          type="warning"
          showIcon
          icon={<WarningFilled />}
          className={styles.whyCallout}
          message={t("Keep these examples up to date.")}
          description={t(
            "These may be included in dispute responses, so if your checkout, policies, emails, or communications change, update them to keep responses accurate."
          )}
        />
      ) : null}
      <div className={styles.sectionFormFields}>
        {groupNodes}
        {leftover.length ? (
          <div className={styles.sectionFormGroupFields}>{leftover.map(renderQ)}</div>
        ) : null}
      </div>
    </Form>
  )
}

export default SectionForm
